// apps/client/src/api/endpoints.ts — one function per §5.1 route (all milestones). Failures throw `HttpError`.
import type { CatalogId } from '@whiskerton/content';                       // revision 9 item 7: devGrant's item ids
import type { AcceptInviteRequest, AcceptInviteResponse, AdminReport, BlockListResponse, ClientErrorReport, ConfigResponse, CreateInviteRequest,
  DevClockRequest, DevOutboxResponse, FoundTownRequest, FoundTownResponse, InviteCode, InviteListResponse, InvitePreviewResponse, InviteView,
  MeResponse, PushPrefsRequest, PushSubscribeRequest, PutProfileRequest, PutProfileResponse, ReportRequest, ResidentId, TicketResponse, TownId,
  TownListEntry, UserId } from '@whiskerton/shared';
import { request, requestRaw } from './http';
import { clearToken, setToken } from './token';

export interface Api {
  health(): Promise<{ ok: true; build: string }>;
  config(): Promise<ConfigResponse>;
  sendCode(email: string): Promise<void>;                                     // POST /api/auth/email-otp/send-verification-otp
  signInWithCode(email: string, otp: string): Promise<{ token: string }>;     // POST /api/auth/sign-in/email-otp; stores the set-auth-token header
  startApple(): Promise<void>;                                                // POST /api/auth/sign-in/social (callbackURL keeps the current path, revision 10 item 9) → navigates to Apple
  finishApple(): Promise<{ token: string } | null>;                           // GET /api/auth/get-session → stores body.session.token (D-44)
  signOut(): Promise<void>;                                                   // POST /api/auth/sign-out; clears the token
  me(): Promise<MeResponse>;
  putProfile(req: PutProfileRequest): Promise<PutProfileResponse>;
  deleteMe(): Promise<void>;
  listTowns(): Promise<{ towns: TownListEntry[] }>;
  foundTown(req: FoundTownRequest): Promise<FoundTownResponse>;
  ticket(townId: TownId): Promise<TicketResponse>;
  listInvites(townId: TownId): Promise<InviteListResponse>;
  createInvite(townId: TownId, req: CreateInviteRequest): Promise<{ invite: InviteView }>;
  revokeInvite(townId: TownId, code: InviteCode): Promise<void>;
  previewInvite(code: InviteCode): Promise<InvitePreviewResponse>;
  acceptInvite(code: InviteCode, req: AcceptInviteRequest): Promise<AcceptInviteResponse>;
  setMayor(townId: TownId, residentId: ResidentId): Promise<void>;
  removeResident(townId: TownId, residentId: ResidentId): Promise<void>;     // own id = leave the town
  vapidKey(): Promise<{ publicKey: string }>;
  subscribePush(req: PushSubscribeRequest): Promise<{ id: string }>;
  unsubscribePush(id: string): Promise<void>;
  putPushPrefs(req: PushPrefsRequest): Promise<void>;
  listBlocks(): Promise<BlockListResponse>;
  block(userId: UserId): Promise<void>;
  unblock(userId: UserId): Promise<void>;
  report(req: ReportRequest): Promise<{ id: string }>;
  adminReports(status: 'open' | 'resolved'): Promise<{ reports: AdminReport[] }>;
  resolveReport(id: string, req: { resolution: 'dismissed' | 'actioned'; note?: string }): Promise<void>;
  clientError(report: ClientErrorReport): Promise<void>;
  devOutbox(email: string): Promise<DevOutboxResponse>;
  devClock(townId: TownId, req: DevClockRequest): Promise<{ now: number }>;
  devRunJobs(townId: TownId): Promise<{ ran: number }>;
  devGrant(townId: TownId, req: { bells?: number; items?: CatalogId[]; museum?: 'full' }): Promise<{ ok: true }>;   // M2-T12 (rev 5), DEV only
}
const enc = encodeURIComponent;
export const api: Api = {
  health: () => request<{ ok: true; build: string }>('GET', '/api/health'),
  config: () => request<ConfigResponse>('GET', '/api/config'),
  sendCode: async (email) => { await request('POST', '/api/auth/email-otp/send-verification-otp', { email, type: 'sign-in' }, { auth: false }); },
  async signInWithCode(email, otp) {
    const res = await requestRaw('POST', '/api/auth/sign-in/email-otp', { email, otp }, { auth: false });
    const token = res.headers.get('set-auth-token');
    if (!token) throw new Error('sign-in returned no token');
    setToken(token);
    return { token };
  },
  async startApple() {
    // Revision 10 item 9: come back to the page we left, so a pending /invite/<code> survives the Apple round trip
    // (boot's history.replaceState keeps the path and phaseScreen opens `invite`).
    const callbackURL = `${location.pathname}?signedIn=1`;
    const r = await request<{ url?: string } | null>('POST', '/api/auth/sign-in/social', { provider: 'apple', callbackURL }, { auth: false, credentials: 'include' });
    if (r?.url) location.assign(r.url);
  },
  async finishApple() {
    const r = await request<{ session?: { token?: string } } | null>('GET', '/api/auth/get-session', undefined, { auth: false, credentials: 'include' });
    const token = r?.session?.token;
    if (!token) return null;
    setToken(token);
    return { token };
  },
  async signOut() { try { await request('POST', '/api/auth/sign-out', {}); } finally { clearToken(); } },
  me: () => request<MeResponse>('GET', '/api/me'),
  putProfile: (req) => request<PutProfileResponse>('PUT', '/api/me/profile', req),
  deleteMe: () => request<void>('DELETE', '/api/me'),
  listTowns: () => request<{ towns: TownListEntry[] }>('GET', '/api/towns'),
  foundTown: (req) => request<FoundTownResponse>('POST', '/api/towns', req),
  ticket: (townId) => request<TicketResponse>('POST', `/api/towns/${townId}/ticket`, {}),
  listInvites: (townId) => request<InviteListResponse>('GET', `/api/towns/${townId}/invites`),
  createInvite: (townId, req) => request<{ invite: InviteView }>('POST', `/api/towns/${townId}/invites`, req),
  revokeInvite: (townId, code) => request<void>('DELETE', `/api/towns/${townId}/invites/${code}`),
  previewInvite: (code) => request<InvitePreviewResponse>('GET', `/api/invites/${code}`),
  acceptInvite: (code, req) => request<AcceptInviteResponse>('POST', `/api/invites/${code}/accept`, req),
  setMayor: async (townId, residentId) => { await request('POST', `/api/towns/${townId}/mayor`, { residentId }); },
  removeResident: (townId, residentId) => request<void>('DELETE', `/api/towns/${townId}/residents/${residentId}`),
  vapidKey: () => request<{ publicKey: string }>('GET', '/api/push/vapid'),
  subscribePush: (req) => request<{ id: string }>('POST', '/api/push/subscriptions', req),
  unsubscribePush: (id) => request<void>('DELETE', `/api/push/subscriptions/${enc(id)}`),
  putPushPrefs: async (req) => { await request('PUT', '/api/push/prefs', req); },
  listBlocks: () => request<BlockListResponse>('GET', '/api/blocks'),
  block: async (userId) => { await request('POST', '/api/blocks', { userId }); },
  unblock: (userId) => request<void>('DELETE', `/api/blocks/${userId}`),
  report: (req) => request<{ id: string }>('POST', '/api/reports', req),
  adminReports: (status) => request<{ reports: AdminReport[] }>('GET', `/api/admin/reports?status=${status}`),
  resolveReport: (id, req) => request<void>('POST', `/api/admin/reports/${enc(id)}/resolve`, req),
  clientError: (report) => request<void>('POST', '/api/client-errors', report, { auth: false }),
  devOutbox: (email) => request<DevOutboxResponse>('GET', `/api/dev/outbox?email=${enc(email)}`),
  devClock: (townId, req) => request<{ now: number }>('POST', `/api/dev/towns/${townId}/clock`, req),
  devRunJobs: (townId) => request<{ ran: number }>('POST', `/api/dev/towns/${townId}/run-jobs`, {}),
  devGrant: (townId, req) => request<{ ok: true }>('POST', `/api/dev/towns/${townId}/grant`, req),   // revision 9 item 7; 404 until M2-T12's route
};
